// src/components/generic/ContentLoadingOverlay/ContentLoadingOverlay.tsx
//
// Reusable loading overlay for "stale while revalidating" UX pattern.
//
// Use this when data is being re-fetched (e.g. after filter changes) and you
// want to keep existing content visible but signal that fresh data is loading.
//
// DO NOT use this for initial page loads — use skeleton loaders for that.
//
// Features:
// - Thin indeterminate progress bar at the top of the content area
// - Semi-transparent overlay that dims existing content
// - Blocks pointer events on content while loading
// - Smooth fade-in/out transitions to avoid flicker on fast responses
// - Configurable opacity, colors, and transition timing
//
// Usage:
//   <ContentLoadingOverlay loading={isRefiltering}>
//     <MyListComponent data={data} />
//   </ContentLoadingOverlay>

import React, { useMemo } from "react";
import { Box, Progress, MantineColor, MantineSize } from "@mantine/core";

// ============================================
// Types
// ============================================

export interface ContentLoadingOverlayProps {
  /** Whether the overlay is visible */
  loading: boolean;

  /** Content to render underneath the overlay */
  children: React.ReactNode;

  /** Opacity of the content while loading (0–1). @default 0.5 */
  contentOpacity?: number;

  /** Color of the progress bar (Mantine color). @default "brand" */
  progressColor?: MantineColor;

  /** Height of the progress bar. @default 3 */
  progressHeight?: number;

  /** Size variant for the progress bar. @default "xs" */
  progressSize?: MantineSize;

  /** Whether the progress bar is animated (striped + animated). @default true */
  animated?: boolean;

  /** Transition duration in ms for fade in/out. @default 200 */
  transitionDuration?: number;

  /** Whether to block pointer events on children while loading. @default true */
  blockInteraction?: boolean;

  /** Additional className for the wrapper */
  className?: string;

  /** Additional style for the wrapper */
  style?: React.CSSProperties;
}

// ============================================
// Component
// ============================================

export const ContentLoadingOverlay: React.FC<ContentLoadingOverlayProps> = ({
  loading,
  children,
  contentOpacity = 0.5,
  progressColor = "brand",
  progressHeight = 3,
  animated = true,
  transitionDuration = 200,
  blockInteraction = true,
  className,
  style,
}) => {
  const transitionValue = `opacity ${transitionDuration}ms ease`;

  const wrapperStyle = useMemo<React.CSSProperties>(
    () => ({
      position: "relative",
      ...style,
    }),
    [style]
  );

  const progressBarStyle = useMemo<React.CSSProperties>(
    () => ({
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 10,
      opacity: loading ? 1 : 0,
      transition: transitionValue,
      pointerEvents: "none",
    }),
    [loading, transitionValue]
  );

  const contentStyle = useMemo<React.CSSProperties>(
    () => ({
      opacity: loading ? contentOpacity : 1,
      transition: transitionValue,
      pointerEvents: loading && blockInteraction ? "none" : "auto",
    }),
    [loading, contentOpacity, transitionValue, blockInteraction]
  );

  return (
    <Box className={className} style={wrapperStyle}>
      {/* Indeterminate progress bar — always rendered for smooth transitions */}
      <Box style={progressBarStyle}>
        <Progress
          value={100}
          h={progressHeight}
          color={progressColor}
          striped
          animated={animated}
          radius={0}
        />
      </Box>

      {/* Content with dimming */}
      <Box style={contentStyle}>
        {children}
      </Box>
    </Box>
  );
};